/* eslint-disable react-refresh/only-export-components */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CircleHelp } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { fetchMyNetwork } from '@/api/network';
import { AddCompanyPopover } from '@/app/(client)/network/-components/AddCompanyPopover';
import { ConnectConfirmDialog } from '@/app/(client)/network/-components/ConnectConfirmDialog';
import { CreateCompanyDialog } from '@/app/(client)/network/-components/CreateCompanyDialog';

const NetworkGraphView = lazy(() =>
  import('@/app/(client)/network/-components/NetworkGraphView').then(m => ({
    default: m.NetworkGraphView,
  })),
);

import { NetworkViewToggle } from '@/app/(client)/network/-components/NetworkViewToggle';
import { RequestNetworkModal } from '@/app/(client)/network/-components/RequestNetworkModal';
import { useAmforiNetworkColumn } from '@/app/(client)/network/-hooks/useAmforiNetworkColumn';
import { myNetworkQueryKey } from '@/app/(client)/network/-hooks/useMyNetwork';
import { useNetworkGraphHandlers } from '@/app/(client)/network/-hooks/useNetworkGraphHandlers';
import { useNetworkMapCompanies } from '@/app/(client)/network/-hooks/useNetworkMapCompanies';
import { useNetworkPage } from '@/app/(client)/network/-hooks/useNetworkPage';
import type { NetworkListSearch } from '@/app/(client)/network/-types';
import { networkListSearch } from '@/app/(client)/network/-types';
import { buildFilterParam } from '@/app/(client)/network/-utils';
import { HelpSheet } from '@/components/shared/HelpSheet';

const NetworkMap = lazy(() =>
  import('@/components/shared/NetworkMap').then(m => ({
    default: m.NetworkMap,
  })),
);

import { ShareNetworkDialog } from '@/components/shared/ShareNetworkDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';

const NetworkTable = lazy(() =>
  import('@/app/(client)/network/-components/NetworkTable').then(m => ({
    default: m.NetworkTable,
  })),
);

export const Route = createFileRoute(
  '/(client)/network/(pages)/(list)/my-network/',
)({
  validateSearch: networkListSearch,
  loaderDeps: ({ search }) => search as NetworkListSearch,
  loader: ({ deps }) => {
    const filter = deps.filters ? buildFilterParam(deps.filters) : undefined;
    const params = {
      page: deps.page,
      limit: deps.limit,
      ...(deps.sort !== undefined ? { sort: deps.sort } : {}),
      ...(filter !== undefined ? { filter } : {}),
    };
    const networkQs = new URLSearchParams({
      isSupplierNetwork: 'true',
      page: String(deps.page - 1),
      count: String(deps.limit),
      filter: filter ?? '{}',
      ...(deps.sort !== undefined ? { sort: deps.sort } : {}),
    });
    return queryClient.ensureQueryData({
      queryKey: myNetworkQueryKey(params),
      queryFn: () => fetchMyNetwork(networkQs.toString()),
    });
  },
  component: MyNetworkPage,
});

function MyNetworkPage() {
  const { getTranslation: t } = useTranslator({ page: 'network' });
  const amforiLastSyncedByCompanyId = useAmforiNetworkColumn();

  const {
    search,
    setView,
    companies,
    total,
    sorting,
    filters,
    rowSelection,
    setRowSelection,
    helpOpen,
    setHelpOpen,
    filterOptions,
    handleSortingChange,
    handleFiltersChange,
    handlePaginationChange,
    handleCellCommit,
    bulkActionsBar,
    unlinkDialog,
    recalcDialog,
    requestModal,
    setRequestModal,
    handleRequestConfirm,
    addCompany,
    connectConfirmDialog,
    createCompanyDialog,
    sharePopover,
  } = useNetworkPage();

  const navigate = useNavigate();
  const networkView = search.view ?? 'table';

  const {
    graph,
    graphLoading,
    graphError,
    isGenerating,
    handleGenerate,
    handleConnect,
    handleEdgeRemove,
    handleNodePositionsChange,
  } = useNetworkGraphHandlers();

  const mapFilter = search.filters
    ? buildFilterParam(search.filters)
    : undefined;
  const mapParams = {
    ...(mapFilter !== undefined ? { filter: mapFilter } : {}),
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
  };
  const { data: mapData, isLoading: mapLoading } =
    useNetworkMapCompanies(mapParams);

  const mapOverlay =
    networkView === 'map' ? (
      <Suspense>
        <NetworkMap companies={mapData?.data ?? []} loading={mapLoading} />
      </Suspense>
    ) : undefined;

  return (
    <>
      <div className='flex-1 min-h-0 flex flex-col overflow-hidden'>
        <div className='flex justify-end mb-2'>
          <NetworkViewToggle value={networkView} onChange={setView} />
        </div>
        {networkView === 'graph' && (
          <Suspense>
            <NetworkGraphView
              graph={graph}
              isLoading={graphLoading}
              isError={graphError}
              isGenerating={isGenerating}
              onNodeClick={id =>
                navigate({
                  to: '/network/$companyId',
                  params: { companyId: id },
                })
              }
              onGenerate={handleGenerate}
              onConnect={handleConnect}
              onEdgeRemove={handleEdgeRemove}
              onNodePositionsChange={handleNodePositionsChange}
            />
          </Suspense>
        )}

        {networkView !== 'graph' && (
          <Suspense>
            <NetworkTable
              data={companies}
              total={total}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              pageIndex={search.page - 1}
              pageSize={search.limit}
              onPaginationChange={handlePaginationChange}
              onCellCommit={handleCellCommit}
              {...(amforiLastSyncedByCompanyId
                ? { amforiLastSyncedByCompanyId }
                : {})}
              {...filterOptions}
              bulkActions={[
                {
                  id: 'unlink',
                  type: 'button',
                  label: t('bulk.unlinkAction'),
                  tooltip: t('bulk.unlink', {
                    label: t('bulk.company', {
                      count: bulkActionsBar.selectedCount,
                    }),
                  }),
                  icon: <i className='fa-light fa-unlink text-sm' />,
                  onClick: bulkActionsBar.onUnlink,
                  loading: bulkActionsBar.isUnlinking,
                  disabled:
                    bulkActionsBar.isUnlinking ||
                    bulkActionsBar.isRequesting ||
                    bulkActionsBar.isExporting ||
                    bulkActionsBar.isRecalculating,
                },
                {
                  id: 'share',
                  type: 'button',
                  label: t('share.trigger'),
                  icon: <i className='fa-light fa-share-nodes text-sm' />,
                  onClick: sharePopover.onOpen,
                  loading: sharePopover.isPending,
                  disabled:
                    bulkActionsBar.isUnlinking ||
                    bulkActionsBar.isRequesting ||
                    bulkActionsBar.isExporting ||
                    bulkActionsBar.isRecalculating,
                },
                {
                  id: 'request-network',
                  type: 'button',
                  label: t('bulk.requestNetworkAction'),
                  tooltip: t('bulk.requestNetwork', {
                    label: t('bulk.company', {
                      count: bulkActionsBar.selectedCount,
                    }),
                  }),
                  icon: <i className='fa-light fa-chart-network text-sm' />,
                  onClick: bulkActionsBar.onRequestNetwork,
                  loading: bulkActionsBar.isRequesting,
                  disabled:
                    bulkActionsBar.isUnlinking ||
                    bulkActionsBar.isRequesting ||
                    bulkActionsBar.isExporting ||
                    bulkActionsBar.isRecalculating,
                },
                {
                  id: 'export',
                  type: 'button',
                  label: t('bulk.exportAction'),
                  tooltip: t('bulk.export', {
                    label: t('bulk.company', {
                      count: bulkActionsBar.selectedCount,
                    }),
                  }),
                  icon: <i className='fa-light fa-file-export text-sm' />,
                  onClick: bulkActionsBar.onExport,
                  loading: bulkActionsBar.isExporting,
                  disabled:
                    bulkActionsBar.isUnlinking ||
                    bulkActionsBar.isRequesting ||
                    bulkActionsBar.isExporting ||
                    bulkActionsBar.isRecalculating,
                },
                {
                  id: 'recalculate',
                  type: 'button',
                  label: t('bulk.recalculateAction'),
                  tooltip: t('bulk.recalculate', {
                    label: t('bulk.company', {
                      count: bulkActionsBar.selectedCount,
                    }),
                  }),
                  icon: <i className='fa-solid fa-arrows-rotate text-sm' />,
                  onClick: bulkActionsBar.onRecalculate,
                  loading: bulkActionsBar.isRecalculating,
                  disabled:
                    bulkActionsBar.isUnlinking ||
                    bulkActionsBar.isRequesting ||
                    bulkActionsBar.isExporting ||
                    bulkActionsBar.isRecalculating,
                },
              ]}
              toolbarActions={[
                {
                  id: 'help',
                  type: 'button' as const,
                  label: t('table.help'),
                  icon: <CircleHelp size={14} />,
                  iconOnly: true as const,
                  variant: 'outline' as const,
                  onClick: () => setHelpOpen(true),
                },
                {
                  id: 'add-company',
                  type: 'custom' as const,
                  content: (
                    <AddCompanyPopover
                      open={addCompany.open}
                      onOpen={addCompany.onOpen}
                      onClose={addCompany.onClose}
                      query={addCompany.query}
                      onQueryChange={addCompany.setQuery}
                      companies={addCompany.companies}
                      hasNextPage={addCompany.hasNextPage}
                      isFetchingNextPage={addCompany.isFetchingNextPage}
                      isLoading={addCompany.isLoading}
                      onFetchNextPage={addCompany.fetchNextPage}
                      onConnectRequest={addCompany.onConnectRequest}
                      isLinking={addCompany.isLinking}
                      confirmOpen={connectConfirmDialog.open}
                      onCreateCompany={addCompany.onCreateCompany}
                    />
                  ),
                },
              ]}
              overlay={mapOverlay}
            />
          </Suspense>
        )}
      </div>

      <HelpSheet
        articleId='8508491-build-your-network-find-add-or-unlink-companies-and-suppliers-to-from-your-network'
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />

      <ShareNetworkDialog
        open={sharePopover.open}
        onClose={sharePopover.onClose}
        sharingWithCompanyIds={sharePopover.sharingWithCompanyIds}
        onShare={sharePopover.onShare}
      />

      <CreateCompanyDialog
        open={createCompanyDialog.open}
        onOpenChange={createCompanyDialog.onOpenChange}
        onCreated={createCompanyDialog.onCreated}
      />

      {/* Unlink confirmation */}
      <AlertDialog
        open={unlinkDialog.open}
        loading={unlinkDialog.isPending}
        onOpenChange={v => !v && unlinkDialog.onClose()}
      >
        <AlertDialogContent>
          <AlertDialogHeader className='flex flex-col gap-3'>
            <div className='flex items-center gap-2.5'>
              <span className='flex items-center justify-center w-8 h-8 rounded-lg bg-destructive/10 shrink-0'>
                <i className='fa-light fa-unlink text-destructive text-sm' />
              </span>
              <AlertDialogTitle>
                {unlinkDialog.selectedCount === 1
                  ? t('unlink.title_one')
                  : t('unlink.title_other', {
                      count: unlinkDialog.selectedCount,
                    })}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              <span className='space-y-3 flex flex-col'>
                <p>
                  {t('unlink.description1')}{' '}
                  <strong className='text-foreground'>
                    {unlinkDialog.selectedCount === 1
                      ? unlinkDialog.companyName ||
                        t('unlink.descriptionCompany_one')
                      : t('unlink.descriptionCompany_other')}
                  </strong>
                  {t('unlink.description2')}
                </p>
                <p>
                  {t('unlink.suggestion')}{' '}
                  <strong className='text-foreground'>
                    {t('unlink.collaborationStatus')}
                  </strong>{' '}
                  {t('unlink.suggestionSuffix')}
                </p>
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unlinkDialog.isPending}>
              {t('unlink.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={unlinkDialog.onConfirm}
              loading={unlinkDialog.isPending}
            >
              {unlinkDialog.isPending
                ? t('unlink.pending')
                : t('unlink.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recalculate confirmation */}
      <AlertDialog
        open={recalcDialog.open}
        loading={recalcDialog.isPending}
        onOpenChange={v => !v && recalcDialog.onClose()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('recalc.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('recalc.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={recalcDialog.isPending}>
              {t('recalc.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              variant='default'
              onClick={recalcDialog.onConfirm}
              loading={recalcDialog.isPending}
            >
              {recalcDialog.isPending
                ? t('recalc.pending')
                : t('recalc.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConnectConfirmDialog
        open={connectConfirmDialog.open}
        company={connectConfirmDialog.company}
        connectionType={connectConfirmDialog.connectionType}
        isPending={connectConfirmDialog.isPending}
        onClose={connectConfirmDialog.onClose}
        onConfirm={connectConfirmDialog.onConfirm}
      />

      {/* Request network modal */}
      <RequestNetworkModal
        open={requestModal.open}
        onClose={() => setRequestModal(prev => ({ ...prev, open: false }))}
        onConfirm={handleRequestConfirm}
        isPending={false}
        pendingCompanies={requestModal.pendingCompanies}
        freshCompanies={requestModal.freshCompanies}
        reRequest={requestModal.reRequest}
        onReRequestChange={v =>
          setRequestModal(prev => ({ ...prev, reRequest: v }))
        }
      />
    </>
  );
}
